// Task screens: list, detail, and the proposal fields on the chat card.
// Design tokens only, same surfaces as the rest of the Operator screens.
import styled from "styled-components";
import { FONT, FIG_INK, FIG_MUTED, SUBTLE, HAIRLINE, CARD_BORDER, PANEL_BORDER, SHADOW_PANEL, RADIUS_PANEL, PRIMARY, TRACK } from "../../design";

export const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px 14px;
  padding: 12px 14px;
  border-bottom: 1px solid ${HAIRLINE};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${SUBTLE};
  input, select {
    font-family: ${FONT};
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: ${FIG_INK};
    border: 1px solid ${CARD_BORDER};
    border-radius: 8px;
    padding: 7px 10px;
    background: #fff;
  }
  input:focus-visible, select:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 1px; }
`;

// Same page shell as the rest of the platform (Launch, Library): a 24px
// gutter from the navigation, anchored left, widening up to 1480px.
export const TaskPage = styled.div`
  font-family: ${FONT};
  color: ${FIG_INK};
  padding: 24px;
  max-width: 1480px;
  box-sizing: border-box;
`;

export const Panel = styled.section`
  background: #fff;
  border: 1px solid ${PANEL_BORDER};
  border-radius: ${RADIUS_PANEL}px;
  box-shadow: ${SHADOW_PANEL};
  overflow: hidden;
  margin-bottom: 16px;
`;

export const PanelHead = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid ${HAIRLINE};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  h2 { font-size: 14px; font-weight: 700; margin: 0; }
  .sp { flex: 1; }
`;

export const TaskRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px 200px;
  gap: 18px;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid ${HAIRLINE};
  cursor: pointer;
  &:hover { background: ${TRACK}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: -2px; }
  .title { font-size: 14px; font-weight: 600; }
  .goal { font-size: 12.5px; color: ${FIG_MUTED}; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { font-size: 12px; color: ${FIG_MUTED}; display: flex; flex-direction: column; gap: 2px; }
  .right { text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; font-size: 12px; color: ${FIG_MUTED}; }
  @media (max-width: 900px) { grid-template-columns: 1fr; .right { align-items: flex-start; text-align: left; } }
`;

export const Stat = styled.div`
  padding: 14px 20px;
  .k { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${SUBTLE}; }
  .v { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; margin-top: 2px; }
  .s { font-size: 12px; color: ${FIG_MUTED}; }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  > div { border-right: 1px solid ${HAIRLINE}; }
  > div:last-child { border-right: none; }
`;

export const RunRow = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${HAIRLINE};
  .hd { display: flex; align-items: center; gap: 10px; font-size: 13px; }
  .hd .n { font-weight: 700; }
  .hd .t { color: ${FIG_MUTED}; font-size: 12px; margin-left: auto; }
  .sig { font-size: 11.5px; color: ${FIG_MUTED}; margin-top: 3px; }
  .dec { font-size: 13px; line-height: 1.5; margin-top: 6px; }
  .acts { font-size: 12.5px; margin-top: 6px; }
  .acts li { margin: 2px 0; }
  .more { all: unset; cursor: pointer; font-size: 12px; color: ${PRIMARY}; margin-top: 6px; display: inline-block; }
  .more:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

export const FieldingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 2fr) minmax(150px, 1fr) 110px 140px 200px;
  gap: 18px;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid ${HAIRLINE};
  cursor: pointer;
  &:hover { background: ${TRACK}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: -2px; }
  .title { font-size: 14px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { font-size: 12px; color: ${FIG_MUTED}; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .attn { font-size: 12px; font-weight: 600; margin-top: 4px; }
  .k { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${SUBTLE}; }
  .v { font-size: 13px; font-variant-numeric: tabular-nums; margin-top: 2px; }
  .right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; font-size: 12px; color: ${FIG_MUTED}; text-align: right; }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
  @media (max-width: 1000px) { grid-template-columns: 1fr 1fr; .right { align-items: flex-start; text-align: left; } }
`;

export const Bar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${TRACK};
  overflow: hidden;
  margin-top: 6px;
  > span { display: block; height: 100%; border-radius: 999px; background: ${(p) => p.$color || PRIMARY}; width: ${(p) => Math.max(0, Math.min(100, p.$pct || 0))}%; }
`;

export const ConfigGrid = styled.dl`
  margin: 0;
  padding: 12px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px 20px;
  font-size: 12.5px;
  dt { color: ${SUBTLE}; font-weight: 600; font-size: 11px; letter-spacing: 0.03em; text-transform: uppercase; }
  dd { margin: 2px 0 0; color: ${FIG_INK}; }
`;

export const ReplyBox = styled.div`
  padding: 12px 20px 14px;
  textarea {
    width: 100%;
    min-height: 64px;
    resize: vertical;
    font-family: ${FONT};
    font-size: 13px;
    line-height: 1.5;
    color: ${FIG_INK};
    border: 1px solid ${CARD_BORDER};
    border-radius: 8px;
    padding: 8px 10px;
    background: #fff;
  }
  textarea:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 1px; }
  .row { display: flex; gap: 10px; align-items: center; margin-top: 8px; flex-wrap: wrap; }
  .hint { font-size: 12px; color: ${FIG_MUTED}; margin-right: auto; }
`;

export const DecisionRow = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${HAIRLINE};
  .hd { display: flex; align-items: center; gap: 10px; font-size: 13px; flex-wrap: wrap; }
  .hd .n { font-weight: 700; }
  .hd .src { font-size: 11px; font-weight: 600; color: ${SUBTLE}; border: 1px solid ${CARD_BORDER}; border-radius: 999px; padding: 1px 8px; }
  .hd .t { color: ${FIG_MUTED}; font-size: 12px; margin-left: auto; }
  .what { font-size: 13px; font-weight: 600; margin-top: 6px; }
  .why { font-size: 13px; line-height: 1.5; margin-top: 4px; }
  .fx { font-size: 12px; color: ${FIG_MUTED}; margin-top: 4px; }
  .ref { font-size: 12.5px; color: ${FIG_MUTED}; margin-top: 4px; font-style: italic; }
`;

export const Policy = styled.dl`
  margin: 0;
  padding: 12px 20px;
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 6px 16px;
  font-size: 12.5px;
  dt { color: ${SUBTLE}; font-weight: 600; }
  dd { margin: 0; color: ${FIG_INK}; }
`;
